// Cliente HTTP do painel: mesma interface e mesma semântica do apisauce 3 (create, get/post/put/delete,
// addAsyncRequestTransform, addResponseTransform...), em cima do axios.
//
// Por que existe: o apisauce faz require("axios"), que no axios 1.x resolve para dist/browser/axios.cjs, e o
// react-scripts 5 (CRA) não reconhece arquivos .cjs: eles viram um "arquivo estático" (o módulo passa a ser uma URL)
// e o painel quebra com "axios.create is not a function". Importando o axios daqui (import ESM) o webpack usa a
// versão correta. Se um dia o CRA for trocado (Vite, etc.), dá para voltar ao apisauce sem mexer em src/api.js.
import axios from "axios";

const DEFAULT_HEADERS = { Accept: "application/json", "Content-Type": "application/json" };
const DEFAULT_CONFIG = { timeout: 0 };

export const NONE = null;
export const CLIENT_ERROR = "CLIENT_ERROR";
export const SERVER_ERROR = "SERVER_ERROR";
export const TIMEOUT_ERROR = "TIMEOUT_ERROR";
export const CONNECTION_ERROR = "CONNECTION_ERROR";
export const NETWORK_ERROR = "NETWORK_ERROR";
export const UNKNOWN_ERROR = "UNKNOWN_ERROR";
export const CANCEL_ERROR = "CANCEL_ERROR";

const TIMEOUT_ERROR_CODES = ["ECONNABORTED"];
const NODEJS_CONNECTION_ERROR_CODES = ["ENOTFOUND", "ECONNREFUSED", "ECONNRESET"];
const STATUS_ERROR_CODES = ["ERR_BAD_REQUEST", "ERR_BAD_RESPONSE"];

const entre = (min, max, valor) => valor >= min && valor <= max;
const em200 = valor => entre(200, 299, valor);
const em400 = valor => entre(400, 499, valor);
const em500 = valor => entre(500, 599, valor);
const ehPromessa = valor => !!valor && (typeof valor === "object" || typeof valor === "function") && typeof valor.then === "function";

export const getProblemFromStatus = status => {
  if (!status) return UNKNOWN_ERROR;
  if (em200(status)) return NONE;
  if (em400(status)) return CLIENT_ERROR;
  if (em500(status)) return SERVER_ERROR;
  return UNKNOWN_ERROR;
};

export const getProblemFromError = erro => {
  if (erro.message === "Network Error") return NETWORK_ERROR;
  if (axios.isCancel(erro)) return CANCEL_ERROR;
  if (!erro.code) return getProblemFromStatus(erro.response ? erro.response.status : null);
  if (STATUS_ERROR_CODES.includes(erro.code)) return getProblemFromStatus(erro.response.status);
  if (TIMEOUT_ERROR_CODES.includes(erro.code)) return TIMEOUT_ERROR;
  if (NODEJS_CONNECTION_ERROR_CODES.includes(erro.code)) return CONNECTION_ERROR;
  return UNKNOWN_ERROR;
};

export const create = config => {
  const cabecalhos = { ...DEFAULT_HEADERS, ...(config.headers || {}) };
  const instancia = config.axiosInstance || axios.create({ ...DEFAULT_CONFIG, ...config, headers: undefined });

  const monitores = [];
  const transformacoesRequisicao = [];
  const transformacoesRequisicaoAsync = [];
  const transformacoesResposta = [];
  const transformacoesRespostaAsync = [];

  const setHeader = (nome, valor) => {
    cabecalhos[nome] = valor;
    return instancia;
  };

  // uma transformação async pode ser uma promessa/função async que já roda, ou uma função que devolve a função async
  // a executar (estilo `request => async () => {...}` usado em src/api.js)
  const executar = async (transformacao, argumento) => {
    const resultado = transformacao(argumento);
    if (ehPromessa(resultado)) await resultado;
    else await resultado(argumento);
  };

  const montarResposta = async (inicio, retorno) => {
    const agora = Date.now();
    const ehErro = retorno instanceof Error || axios.isCancel(retorno);
    const resposta = ehErro ? retorno.response : retorno;
    const status = (resposta && resposta.status) || null;
    const problema = ehErro ? getProblemFromError(retorno) : getProblemFromStatus(status);
    const dados = resposta && resposta.data !== undefined && resposta.data !== null ? resposta.data : null;
    const resultado = {
      duration: agora - inicio,
      problem: problema,
      originalError: ehErro ? retorno : null,
      ok: em200(status),
      status,
      headers: (resposta && resposta.headers) || null,
      config: retorno.config || null,
      data: dados,
    };
    transformacoesResposta.forEach(transformacao => transformacao(resultado));
    for (const transformacao of transformacoesRespostaAsync) await executar(transformacao, resultado);
    return resultado;
  };

  const requisitar = async requisicao => {
    requisicao.headers = { ...cabecalhos, ...requisicao.headers };
    transformacoesRequisicao.forEach(transformacao => transformacao(requisicao));
    for (const transformacao of transformacoesRequisicaoAsync) await executar(transformacao, requisicao);

    const inicio = Date.now();
    const concluir = async retorno => {
      const resposta = await montarResposta(inicio, retorno);
      monitores.forEach(monitor => {
        try { monitor(resposta); } catch (e) { /* um monitor com erro não derruba a requisição */ }
      });
      return resposta;
    };
    // o catch repete o tratamento de propósito (comportamento do apisauce): uma transformação que lança (ex.: lança a
    // resposta de erro) cai aqui e a rejeição final é um objeto de resposta, não um Error
    return instancia.request(requisicao).then(concluir).catch(concluir);
  };

  const semCorpo = metodo => (url, parametros = {}, configAxios = {}) => requisitar({ ...configAxios, url, params: parametros, method: metodo });
  const comCorpo = metodo => (url, dados, configAxios = {}) => requisitar({ ...configAxios, url, method: metodo, data: dados });

  return {
    axiosInstance: instancia,
    monitors: monitores,
    addMonitor: monitor => { monitores.push(monitor); },
    requestTransforms: transformacoesRequisicao,
    asyncRequestTransforms: transformacoesRequisicaoAsync,
    responseTransforms: transformacoesResposta,
    asyncResponseTransforms: transformacoesRespostaAsync,
    addRequestTransform: transformacao => transformacoesRequisicao.push(transformacao),
    addAsyncRequestTransform: transformacao => transformacoesRequisicaoAsync.push(transformacao),
    addResponseTransform: transformacao => transformacoesResposta.push(transformacao),
    addAsyncResponseTransform: transformacao => transformacoesRespostaAsync.push(transformacao),
    setHeader,
    setHeaders: novos => {
      Object.keys(novos).forEach(nome => setHeader(nome, novos[nome]));
      return instancia;
    },
    deleteHeader: nome => {
      delete cabecalhos[nome];
      return instancia;
    },
    headers: cabecalhos,
    setBaseURL: url => {
      instancia.defaults.baseURL = url;
      return instancia;
    },
    getBaseURL: () => instancia.defaults.baseURL,
    any: requisitar,
    get: semCorpo("get"),
    delete: semCorpo("delete"),
    head: semCorpo("head"),
    post: comCorpo("post"),
    put: comCorpo("put"),
    patch: comCorpo("patch"),
  };
};

export default { create };
